import _, {isString, last, uniq} from 'lodash';
import {Edge} from 'reactflow';
import uuid from 'uuid-random';

import {makeNode} from '../../../../libs/shared/core/src/helpers/models/decisionTreeHelpers';
import {
  AbstractDecisionTreeNode,
  DecisionTreeNode,
} from '../../../../libs/shared/core/src/helpers/types/decisionTreeTypes';
import {move} from '../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {
  DecisionTreeData,
  DecisionTreeModel,
  DecisionTreeNodeFrontChat,
  DecisionTreeTypesEnum,
} from '../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeModel';
import {
  ChatbotMessageContent,
  DecisionTreeNodeDataEndFrontChat,
  DecisionTreeNodeDataFrontChat,
  DecisionTreeNodeDataMessage,
  DecisionTreeNodeDataParseExpressionFrontChat,
  DecisionTreeNodeDataSubtreeFrontChat,
  DecisionTreeNodeDataTagFrontChat,
  isBranchNodeDataFrontChat,
  isEndNodeData,
  isPromptReferenceNodeData,
  isStartNodeData,
  NodeDataFrontChatTypesEnum,
} from '../../../../libs/shared/core/src/models/api/decisionTrees/decisionTreeNodeDataFrontChatModel';
import {LayoutOptions} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/decisionTreeLayout';
import {
  findNode,
  gatherLeafNodes,
  getPathToNode,
  getSubtreeIds,
  isEndNode,
  isStartNode,
  isSubtreeNode,
  isTagNode,
  nodeSpacingFromDirection,
} from '../../../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/helpers';
import {flowBuilderDirection} from '../../../../libs/shared/ui/src/components/decisionTrees/flowBuilder/constants';
import {Expression} from '../../../settings/editors/expressions/expressionModel';
import {maxBranchesPerNode} from './constants';
import {
  buildDefaultParseExpressionOptions,
  computeParseExpressionResultType,
} from './expressions/expressionHelpers';
import {
  ChatBotBuilderAbstractNode,
  ChatBotBuilderNode,
  ChatBotBuilderNodeData,
  ExitNodeLabelMap,
} from './types';

/*
 * Enums.
 */

// WARNING: Never mutate the values in this enum. These values are expected downstream in front-chat
// so that the appropriate labels can be rendered, and the appropriate analytics metadata can be sent.
export enum SemanticSearchOptionsEnum {
  YES = 'Yes',
  NO = 'No',
}

// defines the way end nodes can terminate
export enum EndNodeDataFrontChatTypesEnum {
  TRANSFER = 'TRANSFER',
  ARCHIVE = 'ARCHIVE',
  LIVE_CHAT = 'LIVE_CHAT',
}

export enum OnlineHoursOptionsEnum {
  ONLINE = 'Online',
  OFFLINE = 'Offline',
}

/*
 * Constants.
 */

/**
 * Specifies the direction of the flow builder in addition to the custom spacing logic that is needed
 * for certain node data types.
 */
export const chatBotBuilderLayoutOptions: LayoutOptions<DecisionTreeNodeDataFrontChat> = {
  direction: flowBuilderDirection,
  computeNodeSize: (data: DecisionTreeNodeDataFrontChat): [number, number] => {
    const nodeSpacing = nodeSpacingFromDirection(flowBuilderDirection);

    // We halve the spacing from the start node for overall visual consistency with other nodes.
    if (data.type === NodeDataFrontChatTypesEnum.START) {
      return [nodeSpacing[0], nodeSpacing[1] / 2];
    }

    // Multi Choice nodes have their vertical distance increased with respect to the number of options.
    if (isBranchNodeDataFrontChat(data)) {
      const spacingMultiplier = 1 + data.options.length * 0.2;
      return [nodeSpacing[0] * spacingMultiplier, nodeSpacing[1]];
    }

    if (isEndNodeData(data)) {
      // End nodes that transfer can have error messages & channel information, so we add 50% more space.
      const transferSpace =
        (computeEndNodeType(data, true) === EndNodeDataFrontChatTypesEnum.TRANSFER ? 0.5 : 0) *
        nodeSpacing[0];

      // By default we add 50% more space to end nodes.
      return [nodeSpacing[0] * 1.5 + transferSpace, nodeSpacing[1]];
    }

    // Custom Field nodes have their vertical distance doubled, due to the larger maximum height.
    if (data.type === NodeDataFrontChatTypesEnum.CUSTOM_FIELDS) {
      return [nodeSpacing[0] * 2, nodeSpacing[1]];
    }

    return nodeSpacing;
  },
};

/*
 * Helpers.
 */

export const makeChatBotBuilderNode = makeNode<ChatBotBuilderNodeData>;

export function makeChatBotBuilderDefaultNodeData(
  nodeDataType: NodeDataFrontChatTypesEnum,
  pathToParentNode: ReadonlyArray<ChatBotBuilderNode> | undefined,
  expressions: ReadonlyArray<Expression>,
): ChatBotBuilderNodeData {
  switch (nodeDataType) {
    case NodeDataFrontChatTypesEnum.MESSAGE:
      return {
        type: NodeDataFrontChatTypesEnum.MESSAGE,
        message: '',
      };
    case NodeDataFrontChatTypesEnum.MULTI_CHOICE:
      return {
        type: NodeDataFrontChatTypesEnum.MULTI_CHOICE,
        message: '',
        options: [
          {
            value: '',
            sourceHandle: uuid(),
          },
          {
            value: '',
            sourceHandle: uuid(),
          },
        ],
      };
    case NodeDataFrontChatTypesEnum.PROMPT:
      return {
        type: NodeDataFrontChatTypesEnum.PROMPT,
        message: '',
      };
    case NodeDataFrontChatTypesEnum.CUSTOM_FIELDS:
      return {type: NodeDataFrontChatTypesEnum.CUSTOM_FIELDS, customFields: []};
    case NodeDataFrontChatTypesEnum.PARSE_TEXT: {
      const lastPromptId = findLastPromptId(pathToParentNode);

      return {
        type: NodeDataFrontChatTypesEnum.PARSE_TEXT,
        promptNodeId: lastPromptId,
        options: [
          {
            value: [],
            sourceHandle: uuid(),
          },
          {
            value: [],
            sourceHandle: uuid(),
          },
        ],
      };
    }
    case NodeDataFrontChatTypesEnum.TAG:
      return {
        type: NodeDataFrontChatTypesEnum.TAG,
        tagIds: [],
      };
    case NodeDataFrontChatTypesEnum.SEMANTIC_SEARCH: {
      const lastPromptId = findLastPromptId(pathToParentNode);

      return {
        type: NodeDataFrontChatTypesEnum.SEMANTIC_SEARCH,
        promptNodeId: lastPromptId,
        options: [
          {
            value: SemanticSearchOptionsEnum.YES,
            sourceHandle: uuid(),
          },
          {
            value: SemanticSearchOptionsEnum.NO,
            sourceHandle: uuid(),
          },
        ],
      };
    }
    case NodeDataFrontChatTypesEnum.SUBTREE:
      return makeDefaultSubtreeNodeData();
    case NodeDataFrontChatTypesEnum.PARSE_EXPRESSION: {
      const firstExpression = expressions[0];
      if (!firstExpression) {
        throw new Error('Expect to have at least one expression to create a parse expression node');
      }

      const resultType = computeParseExpressionResultType(firstExpression);

      return {
        type: NodeDataFrontChatTypesEnum.PARSE_EXPRESSION,
        expressionId: firstExpression?.id,
        resultType,
        options: buildDefaultParseExpressionOptions(resultType),
      };
    }
    case NodeDataFrontChatTypesEnum.ONLINE_HOURS:
      return {
        type: NodeDataFrontChatTypesEnum.ONLINE_HOURS,
        options: [
          {
            value: OnlineHoursOptionsEnum.ONLINE,
            sourceHandle: uuid(),
          },
          {
            value: OnlineHoursOptionsEnum.OFFLINE,
            sourceHandle: uuid(),
          },
        ],
      };
    default:
      throw new Error(`Cannot create node data of type: ${nodeDataType}`);
  }
}

export function gatherSubtreeExitNodes(
  subtree: DecisionTreeModel,
): ReadonlyArray<DecisionTreeNode<DecisionTreeNodeDataEndFrontChat>> {
  if (subtree && subtree.treeType !== DecisionTreeTypesEnum.SUBTREE) {
    throw new Error('Only use for subtree types');
  }

  if (!subtree.treeData) {
    return [] as ReadonlyArray<DecisionTreeNode<DecisionTreeNodeDataEndFrontChat>>;
  }

  return gatherLeafNodes(subtree.treeData.nodes, subtree.treeData.edges) as ReadonlyArray<
    DecisionTreeNode<DecisionTreeNodeDataEndFrontChat>
  >;
}

export function computeExitNodeLabelMap(subtree: DecisionTreeModel): ExitNodeLabelMap {
  const subtreeExitNodes = gatherSubtreeExitNodes(subtree);
  return subtreeExitNodes.reduce((map, subtreeExitNode) => {
    const label =
      subtreeExitNode.data.type === NodeDataFrontChatTypesEnum.END ? subtreeExitNode.data.label : undefined;
    if (label) {
      map.set(subtreeExitNode.id, label);
    }
    return map;
  }, new Map<string, string | undefined>());
}

/**
 * Default subtree nodes only contains 1 option.
 */
export function makeDefaultSubtreeNodeData(): DecisionTreeNodeDataSubtreeFrontChat {
  return {
    type: NodeDataFrontChatTypesEnum.SUBTREE,
    options: [{sourceHandle: uuid()}],
  };
}

export function makeParseExpressionNodeData(
  expression: Expression,
): DecisionTreeNodeDataParseExpressionFrontChat {
  return {
    type: NodeDataFrontChatTypesEnum.PARSE_EXPRESSION,
    expressionId: expression.id,
    resultType: computeParseExpressionResultType(expression),
    options: buildDefaultParseExpressionOptions(computeParseExpressionResultType(expression)),
  };
}

/**
 * Find the most recent prompt node in this branch if it exists.
 */
export function findLastPromptId(pathToParentNode?: ReadonlyArray<ChatBotBuilderNode>) {
  if (!pathToParentNode) {
    return undefined;
  }

  return (
    _(pathToParentNode).findLast((node) => node.data.type === NodeDataFrontChatTypesEnum.PROMPT)?.id ??
    undefined
  );
}

/**
 * Given a node that references a prompt node, re-compute its promptNodeId. After a PROMPT node is added or deleted,
 * each referring node may have to update this value as these node types automatically use the most recent PROMPT node in their branch.
 *
 * We may optionally ignore a prompt node if it has been deleted and it should not appear as a result from findLastPromptId.
 */

export function maybeUpdatePromptReference(
  node: ChatBotBuilderNode,
  nodes: Array<ChatBotBuilderNode>,
  edges: Array<Edge>,
) {
  // no need to update promptNodeId for nodes that are not prompt references
  if (!isPromptReferenceNodeData(node.data)) {
    return node;
  }

  const path = getPathToNode(node, nodes, edges);
  const promptNodeId = findLastPromptId(path);
  const updatedPromptReferenceNode = {
    ...node,
    data: {
      ...node.data,
      promptNodeId,
    },
  };

  return updatedPromptReferenceNode;
}

/**
 * Returns only prompt reference nodes after re-computing its promptNodeId.
 */
export function computePromptNodeIdForPromptReferenceNodes(
  nodes: Array<ChatBotBuilderNode>,
  edges: Array<Edge>,
) {
  const promptReferenceNodes = nodes.filter((node) => isPromptReferenceNodeData(node.data));
  // Filter through nodes and update only the prompt reference nodes with a potential new prompt node.
  return promptReferenceNodes.map((node) => maybeUpdatePromptReference(node, nodes, edges));
}

/**
 * Gather any prompt reference nodes without a prompt earlier in its path.
 *
 * Note: A subtree can be saved with a prompt reference node without a prompt, and this check will then return the bad nodes for that subtree.
 */
export function gatherPromptReferenceNodesWithoutPrompt(decisionTree?: DecisionTreeModel) {
  if (!decisionTree || !decisionTree.treeData) {
    return [];
  }

  const {nodes, edges} = decisionTree.treeData;

  return nodes.filter((node) => {
    if (!isPromptReferenceNodeData(node.data)) {
      return false;
    }
    const path = getPathToNode(node, nodes.slice(), edges.slice());
    const promptNode = findLastPromptId(path);
    return !promptNode;
  });
}

/**
 * Given a list of nodes, retrieve a unique and flattened list of all tag ids that
 * exist among all nodes with a data type of TAG.
 */
function gatherNodesTagIds(nodes: ReadonlyArray<DecisionTreeNodeFrontChat>) {
  return _(nodes)
    .map((pathNode) => pathNode.data)
    .filter(
      (nodeData): nodeData is DecisionTreeNodeDataTagFrontChat =>
        nodeData.type === NodeDataFrontChatTypesEnum.TAG,
    )
    .map((nodeData) => nodeData.tagIds)
    .flatten()
    .uniq()
    .value();
}

/**
 * Uses gatherNodesTagIds, but also includes the tag ids from any subtree nodes or any subtree tags that may apply
 */
export function gatherNodeTagIdsIncludeSubtrees(
  nodes: ReadonlyArray<DecisionTreeNodeFrontChat>,
  subtrees: ReadonlyArray<DecisionTreeModel>,
) {
  const {tagIds, maybeTagIds} = gatherSubtreeTags(nodes, subtrees);

  return {
    tagIds: uniq([...gatherNodesTagIds(nodes), ...tagIds]),
    maybeTagIds: uniq(maybeTagIds),
  };
}

function gatherSubtreeTags(
  nodes: ReadonlyArray<DecisionTreeNodeFrontChat>,
  subtrees: ReadonlyArray<DecisionTreeModel>,
) {
  const subtreeIds = getSubtreeIds(nodes);
  const subtreeTags: Array<number> = [];
  const subtreeMaybeTags: Array<number> = [];

  subtreeIds.forEach((subtreeId) => {
    const subtree = subtrees.find((s) => s.id === subtreeId);
    // if a branch node is found, then all tags after it
    // are 'maybe tags' (tags that maybe apply depending on the path the user takes)
    let isAfterBranchNode = false;

    if (!subtree) {
      return;
    }

    subtree.treeData?.nodes.forEach((node) => {
      if (isBranchNodeDataFrontChat(node.data)) {
        isAfterBranchNode = true;
        return;
      }
      if (isTagNode(node)) {
        const tagList = isAfterBranchNode ? subtreeMaybeTags : subtreeTags;
        tagList.push(...node.data.tagIds);
      }
    });
  });

  return {
    tagIds: subtreeTags,
    maybeTagIds: subtreeMaybeTags,
  };
}

/**
 * Move an item in an array to a new index, while preserving the values at each location of the array
 * for the keys specified in keysToPreserve.
 */
export function moveAndPreserve<T>(
  array: ReadonlyArray<T>,
  newIndex: number,
  item: T,
  keysToPreserve: ReadonlyArray<keyof T>,
) {
  // For each item in array, build a partial object of the preserved values to spread back in later.
  const preservedValuesArray: ReadonlyArray<Partial<T>> = array.map((arrayItem) =>
    keysToPreserve.reduce<Partial<T>>(
      (preservedValues, key) => ({...preservedValues, [key]: arrayItem[key]}),
      {},
    ),
  );

  const movedArray = move(array, newIndex, item);

  // Spread the preserved values back into each item of the moved array before returning.
  return movedArray.map((arrayItem, index) => {
    const heldValues = preservedValuesArray[index];
    return {...arrayItem, ...heldValues};
  });
}

export function computeNumSteps(decisionTree: DecisionTreeModel) {
  return (
    decisionTree.treeData?.nodes.filter((node) => !isStartNodeData(node.data) && !isEndNodeData(node.data))
      .length ?? 0
  );
}

/** Checks if the most recent node on the path leading up to a node is a certain type. */
export function isLastNodeOfType(
  nodeDataType: NodeDataFrontChatTypesEnum,
  pathToParentNode?: ReadonlyArray<ChatBotBuilderNode>,
) {
  return last(pathToParentNode)?.data.type === nodeDataType;
}

/** Checks if one of the direct children of a node is a certain type. */
export function isSomeChildNodesOfType(
  nodeDataType: NodeDataFrontChatTypesEnum,
  childNodeDescendantPaths: ReadonlyArray<ReadonlyArray<ChatBotBuilderAbstractNode>>,
): boolean {
  return childNodeDescendantPaths.some((path) => path[0]?.data.type === nodeDataType);
}

export function computeEndNodeType(
  endNodeData?: DecisionTreeNodeDataEndFrontChat,
  hasTransferChannel?: boolean,
): EndNodeDataFrontChatTypesEnum | undefined {
  if (!endNodeData) {
    return undefined;
  }

  // check explicitly for 0 here which indicates that the user intends to transfer but has not selected a channel
  const isTransferring =
    (hasTransferChannel && Boolean(endNodeData?.transferToChannelId)) ||
    endNodeData?.transferToChannelId === 0;
  if (isTransferring) {
    return EndNodeDataFrontChatTypesEnum.TRANSFER;
  }

  const isArchiving = Boolean(endNodeData?.shouldEndConversation);
  if (isArchiving) {
    return EndNodeDataFrontChatTypesEnum.ARCHIVE;
  }

  return EndNodeDataFrontChatTypesEnum.LIVE_CHAT;
}

export function computeHasOnlyEndChildren(
  childNodes: Array<DecisionTreeNode<DecisionTreeNodeDataFrontChat>>,
) {
  return childNodes.every((node) => node.data.type === NodeDataFrontChatTypesEnum.END);
}

export function toMessageTemplate(value: DecisionTreeNodeDataMessage): ChatbotMessageContent {
  if (isString(value)) {
    return {
      template: [
        {
          text: value,
        },
      ],
    };
  }

  return {
    template: value.template.map((messagePart) => {
      if (isString(messagePart)) {
        return {
          text: messagePart,
        };
      }
      return messagePart;
    }),
  };
}

export function countStepsInSubtreePath(exitNodeId: string, subtree: DecisionTreeModel | undefined) {
  const subtreeNodes = subtree?.treeData?.nodes;
  const subtreeEdges = subtree?.treeData?.edges;

  if (!subtreeNodes || !subtreeEdges) {
    return 0;
  }

  const exitNode = findNode([...subtreeNodes], exitNodeId);

  if (!exitNode) {
    return 0;
  }

  return getPathToNode(exitNode, [...subtreeNodes], [...subtreeEdges]).filter(
    (node) => !isStartNode(node) && !isEndNode(node),
  ).length;
}

export function computeHasChatbotSubtreeBranchErrors(
  chatbotTreeData: DecisionTreeData,
  subtrees: ReadonlyArray<DecisionTreeModel>,
) {
  const subtreeNodes = chatbotTreeData.nodes.filter(isSubtreeNode);

  const hasUnreachableBranches = subtreeNodes.some((subtreeNode) =>
    computeHasUnreachableBranches(subtreeNode, subtrees),
  );
  const hasNewExitsMissingBranch = subtreeNodes.some((subtreeNode) =>
    computeHasNewExitsMissingBranch(subtreeNode, subtrees),
  );

  return hasUnreachableBranches || hasNewExitsMissingBranch;
}

export function computeHasUnreachableBranches(
  subtreeNode: DecisionTreeNode<DecisionTreeNodeDataSubtreeFrontChat>,
  subtrees: ReadonlyArray<DecisionTreeModel>,
) {
  if (!subtreeNode.data.hasSplitExits) {
    return false;
  }

  const subtree = subtrees.find((s) => s.id === subtreeNode.data.decisionTreeId);

  // When chatbot editor first loads subtrees are undefined, and then load later.
  if (!subtree) {
    return false;
  }

  const subtreeExitNodes = subtree ? gatherSubtreeExitNodes(subtree) : [];
  const exitNodeIds = subtreeExitNodes.map((node) => node.id);

  return subtreeNode.data.options.some((option) => !exitNodeIds?.includes(option?.exitNodeId ?? ''));
}

/**
 * To ensure unlabeled exit nodes always have the same placeholder label (i.e. Path A) inside the
 * Saved Flow exit node & in the Chatbot builder subtree node options, we need a deterministic sorting
 * function to eventually map exitNodeIds to letters (A, B, C, etc). This function sorts exit nodes
 * alphabetically by their id, which means that while they can be reshuffled when new exits are added,
 * Path D inside a saved flow will always map to the correct Path D in the Chatbot builder.
 */
export function computeSortedExitNodeIds(
  abstractDecisionTreeNodes: ReadonlyArray<AbstractDecisionTreeNode<DecisionTreeNodeDataFrontChat>>,
) {
  const endNodes = abstractDecisionTreeNodes.filter(
    (node) => node.data.type === NodeDataFrontChatTypesEnum.END,
  );
  return [...endNodes].sort((a, b) => a.id.localeCompare(b.id)).map((exitNode) => exitNode.id);
}

export function computeHasNewExitsMissingBranch(
  subtreeNode: DecisionTreeNode<DecisionTreeNodeDataSubtreeFrontChat>,
  subtrees: ReadonlyArray<DecisionTreeModel>,
) {
  if (!subtreeNode.data.hasSplitExits) {
    return false;
  }

  const subtree = subtrees.find((s) => s.id === subtreeNode.data.decisionTreeId);

  // When chatbot editor first loads subtrees are undefined, and then load later.
  if (!subtree) {
    return false;
  }

  const subtreeExitNodes = subtree ? gatherSubtreeExitNodes(subtree) : [];
  const exitNodesMissingBranch = subtreeExitNodes.filter(
    (subtreeExitNode) =>
      subtreeNode.data.type === NodeDataFrontChatTypesEnum.SUBTREE &&
      !subtreeNode.data.options.some((option) => option.exitNodeId === subtreeExitNode.id),
  );

  return Boolean(exitNodesMissingBranch.length);
}

export function convertIndexToLetter(index: number) {
  return String.fromCharCode('A'.charCodeAt(0) + index);
}

export function computeIsSubtreeBranchable(
  nodes: ReadonlyArray<DecisionTreeNode<DecisionTreeNodeDataFrontChat>>,
  edges: ReadonlyArray<Edge>,
) {
  return gatherLeafNodes(nodes, edges).length <= maxBranchesPerNode;
}

export function computeHasMaxSubtreeExits(
  nodes: ReadonlyArray<DecisionTreeNode<DecisionTreeNodeDataFrontChat>>,
  edges: ReadonlyArray<Edge>,
) {
  return gatherLeafNodes(nodes, edges).length >= maxBranchesPerNode;
}
